import React from "react";
import { ReportAssignmentRow, ReportTypeDefinition } from "./types";
import { apiErrorMessage, fractionFromStatus } from "./logic";
import { useReportReadiness } from "./useReportAssignmentWorkspace";
import { RaIcon } from "./RaIcon";

interface DataReadinessModalProps {
  row: ReportAssignmentRow;
  reportType: ReportTypeDefinition;
  bomName: string;
  onClose: () => void;
  onGoToAssignment: (row: ReportAssignmentRow) => void;
  onOpenTraveler: (serial: string) => void;
}

/**
 * Section 3's drill-down: per-module data-readiness detail for a single
 * report (GET assignments/<id>/readiness/), with a link out to that
 * module's Virtual Traveler record.
 */
export const DataReadinessModal: React.FC<DataReadinessModalProps> = ({
  row,
  reportType,
  bomName,
  onClose,
  onGoToAssignment,
  onOpenTraveler,
}) => {
  const readiness = useReportReadiness(row.id);
  const detail = readiness.data;
  const items = detail?.modules ?? [];

  const pg = detail?.progress ?? row.progress;
  const goal = detail?.criterion ?? reportType.goal;
  const left = pg.total - pg.done;
  const pct = pg.total ? Math.round((pg.done / pg.total) * 100) : 0;
  const todo = items.filter((x) => !x.done);
  const done = items.filter((x) => x.done);

  const renderRow = (x: (typeof items)[number]) => {
    const f = fractionFromStatus(x.current);
    return (
      <div key={x.id} className={`dsr${x.done ? " isdone" : ""}`}>
        <button
          type="button"
          className="vt mono"
          title={`Open ${x.serial} in Virtual Traveler`}
          onClick={() => onOpenTraveler(x.serial)}
        >
          {x.serial}
          <RaIcon name="ext" />
        </button>
        <span className="cur">
          {x.current}
          {f !== null && !x.done && (
            <span className="mbar">
              <i style={{ width: `${Math.round(f * 100)}%` }} />
            </span>
          )}
        </span>
        <span className="goal">{x.goal}</span>
        <span className={`cs2 ${x.done ? "ok" : "no"}`}>
          <RaIcon name={x.done ? "check" : "clock"} />
          {x.done ? "Done" : "Not done"}
        </span>
      </div>
    );
  };

  const group = (label: string, list: typeof items, cls: string) =>
    list.length ? (
      <>
        <div className={`dsg ${cls}`}>
          <span>{label}</span>
          <em>{list.length}</em>
        </div>
        {list.map(renderRow)}
      </>
    ) : null;

  return (
    <>
      <div className="scrim open" onClick={onClose} />
      <div className="modal wide open" role="dialog" aria-modal="true" aria-label="Data status">
        <div className="dm-h">
          <div>
            <div className="dm-eyebrow">Data readiness</div>
            <h3>
              {reportType.name} <span>· {bomName}</span>
            </h3>
          </div>
          <button type="button" className="ib" aria-label="Close" onClick={onClose}>
            <RaIcon name="x" />
          </button>
        </div>

        <div className={`dm-sum ${pg.ready ? "ok" : "wait"}`}>
          <div className="dm-st">
            <span className="dm-ic">
              <RaIcon name={pg.ready ? "check" : "clock"} />
            </span>
            <div>
              <b>
                {pg.ready
                  ? row.confirmed
                    ? "Ready to write"
                    : "Data ready"
                  : `Waiting for ${left} ${left === 1 ? "module" : "modules"}`}
              </b>
              <span>
                {pg.done} of {pg.total} modules done
              </span>
            </div>
            <span className="dm-pct">{pct}%</span>
          </div>
          <div className="dm-bar">
            <i style={{ width: `${pct}%` }} />
          </div>
          {pg.ready &&
            !pg.none &&
            (row.confirmed ? (
              <div className="dm-next ok">
                <RaIcon name="lock" />
                <span>
                  <b>Modules confirmed.</b> This report goes to the writer’s agenda.
                </span>
              </div>
            ) : (
              <div className="dm-next warn">
                <RaIcon name="alert" />
                <span>
                  <b>Modules not confirmed yet.</b> Confirm them in section 2 to send this report
                  to the writer’s agenda.
                </span>
              </div>
            ))}
          <div className="dm-crit">
            <span>Criterion</span>
            <b>{reportType.dataReadyCriterion}</b>
            <span className="dot-sep">·</span>
            <span>every module needs to reach</span>
            <b>{goal}</b>
          </div>
        </div>

        <div className="dst">
          <div className="dsh">
            <span>Serial number</span>
            <span>Where it is now</span>
            <span>Needs to reach</span>
            <span>Criteria status</span>
          </div>
          <div className="dsl">
            {readiness.isLoading && <div className="hempty">Loading modules…</div>}
            {readiness.isError && (
              <div className="hempty">
                {apiErrorMessage(readiness.error, "Could not load the module detail.")}
              </div>
            )}
            {group("Not done yet", todo, "g-no")}
            {group("Done", done, "g-ok")}
            {pg.none && (
              <div className="hempty">
                No modules needed for this report. It is written directly from the BOM and
                production details.
              </div>
            )}
          </div>
        </div>

        <div className="mf dm-f">
          <span className="dm-hint">
            <RaIcon name="ext" />
            Click a serial number to open it in Virtual Traveler
          </span>
          <span style={{ flex: 1 }} />
          {pg.ready && !row.confirmed && !pg.none ? (
            <>
              <button type="button" className="btn sm" onClick={onClose}>
                Close
              </button>
              <button type="button" className="btn sm pr" onClick={() => onGoToAssignment(row)}>
                <RaIcon name="check" />
                Confirm modules in section 2
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn sm" onClick={() => onGoToAssignment(row)}>
                View in section 2
              </button>
              <button type="button" className="btn sm pr" onClick={onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};
